import { OutboundPlanningGridRow, OutboundPlanningMetrics } from "./useOutboundPlanningGridData";

export type FreightGroupKey =
  | "inHouse"
  | "inbound"
  | "pickupsUnassigned"
  | "pickupsAssigned"
  | "pickupsCompleted";

export const DEFAULT_FREIGHT_INCLUDES: Record<FreightGroupKey, boolean> = {
  inHouse: true,
  inbound: true,
  pickupsUnassigned: true,
  pickupsAssigned: true,
  pickupsCompleted: true,
};

/** Whether the aggregate "Pickups" column is fully included
 *  (all 3 pickup sub-types are checked). Drives the header checkbox's
 *  checked / indeterminate state and the column's dim state. */
export function getPickupsAggregateState(included: Record<FreightGroupKey, boolean>): {
  allChecked: boolean;
  noneChecked: boolean;
  indeterminate: boolean;
} {
  const all =
    included.pickupsUnassigned && included.pickupsAssigned && included.pickupsCompleted;
  const none =
    !included.pickupsUnassigned && !included.pickupsAssigned && !included.pickupsCompleted;
  return { allChecked: all, noneChecked: none, indeterminate: !all && !none };
}

export type SortDirection = "asc" | "desc";

export type SortableMetricGroup =
  | "totals"
  | "inHouse"
  | "inbound"
  | "pickupsAggregate"
  | "pickupsUnassigned"
  | "pickupsAssigned"
  | "pickupsCompleted";

export type MetricColumn = "shipments" | "pieces" | "weight";

export type SortKey =
  | { kind: "destination" }
  | { kind: "metric"; group: SortableMetricGroup; metric: MetricColumn };

export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

export const DEFAULT_SORT: SortState = {
  key: { kind: "destination" },
  direction: "asc",
};

export function sortKeysEqual(a: SortKey, b: SortKey): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "destination" && b.kind === "destination") return true;
  if (a.kind === "metric" && b.kind === "metric") {
    return a.group === b.group && a.metric === b.metric;
  }
  return false;
}

function sumMetrics(...parts: OutboundPlanningMetrics[]): OutboundPlanningMetrics {
  return parts.reduce(
    (acc, m) => ({
      shipments: acc.shipments + m.shipments,
      pieces: acc.pieces + m.pieces,
      weight: acc.weight + m.weight,
    }),
    { shipments: 0, pieces: 0, weight: 0 }
  );
}

/** Totals column = sum of every included freight type. Pickup sub-types are
 *  toggled independently; the "Pickups" header is a select-all UI for them. */
export function computeAdjustedTotals(
  row: OutboundPlanningGridRow,
  included: Record<FreightGroupKey, boolean>
): OutboundPlanningMetrics {
  const parts: OutboundPlanningMetrics[] = [];
  if (included.inHouse) parts.push(row.inHouse);
  if (included.inbound) parts.push(row.inbound);
  if (included.pickupsUnassigned) parts.push(row.pickupsUnassigned);
  if (included.pickupsAssigned) parts.push(row.pickupsAssigned);
  if (included.pickupsCompleted) parts.push(row.pickupsCompleted);
  return sumMetrics(...parts);
}

/** Aggregate "Pickups" column = sum of currently-included pickup sub-types. */
export function computeIncludedPickupsTotal(
  row: OutboundPlanningGridRow,
  included: Record<FreightGroupKey, boolean>
): OutboundPlanningMetrics {
  const parts: OutboundPlanningMetrics[] = [];
  if (included.pickupsUnassigned) parts.push(row.pickupsUnassigned);
  if (included.pickupsAssigned) parts.push(row.pickupsAssigned);
  if (included.pickupsCompleted) parts.push(row.pickupsCompleted);
  return sumMetrics(...parts);
}

function getMetricForGroup(
  row: OutboundPlanningGridRow,
  group: SortableMetricGroup,
  adjustedTotals: OutboundPlanningMetrics
): OutboundPlanningMetrics {
  switch (group) {
    case "totals":
      return adjustedTotals;
    case "inHouse":
      return row.inHouse;
    case "inbound":
      return row.inbound;
    case "pickupsAggregate":
      return row.pickupsTotal;
    case "pickupsUnassigned":
      return row.pickupsUnassigned;
    case "pickupsAssigned":
      return row.pickupsAssigned;
    case "pickupsCompleted":
      return row.pickupsCompleted;
  }
}

export function compareRowsForSort(
  a: OutboundPlanningGridRow,
  b: OutboundPlanningGridRow,
  sort: SortState,
  totalsA: OutboundPlanningMetrics,
  totalsB: OutboundPlanningMetrics
): number {
  const dir = sort.direction === "asc" ? 1 : -1;
  if (sort.key.kind === "destination") {
    return a.terminalName.localeCompare(b.terminalName) * dir;
  }
  const { group, metric } = sort.key;
  const va = getMetricForGroup(a, group, totalsA)[metric];
  const vb = getMetricForGroup(b, group, totalsB)[metric];
  if (va === vb) return a.terminalName.localeCompare(b.terminalName);
  return (va - vb) * dir;
}

export interface HubDefinition {
  id: string;
  name: string;
  terminalNumbers: number[];
}

export interface HubGroupedSection {
  hubId: string | null;
  hubName: string;
  rows: OutboundPlanningGridRow[];
}

export interface HubSectionTotals {
  totals: OutboundPlanningMetrics;
  inHouse: OutboundPlanningMetrics;
  inbound: OutboundPlanningMetrics;
  /** Sum of CURRENTLY-INCLUDED pickup sub-types (mirrors the "Pickups" column display). */
  pickupsTotal: OutboundPlanningMetrics;
  pickupsUnassigned: OutboundPlanningMetrics;
  pickupsAssigned: OutboundPlanningMetrics;
  pickupsCompleted: OutboundPlanningMetrics;
}

const ZERO: OutboundPlanningMetrics = { shipments: 0, pieces: 0, weight: 0 };

export function computeHubSectionTotals(
  rows: OutboundPlanningGridRow[],
  included: Record<FreightGroupKey, boolean>
): HubSectionTotals {
  const acc: HubSectionTotals = {
    totals: { ...ZERO },
    inHouse: { ...ZERO },
    inbound: { ...ZERO },
    pickupsTotal: { ...ZERO },
    pickupsUnassigned: { ...ZERO },
    pickupsAssigned: { ...ZERO },
    pickupsCompleted: { ...ZERO },
  };

  const addInto = (target: OutboundPlanningMetrics, m: OutboundPlanningMetrics) => {
    target.shipments += m.shipments;
    target.pieces += m.pieces;
    target.weight += m.weight;
  };

  for (const row of rows) {
    addInto(acc.inHouse, row.inHouse);
    addInto(acc.inbound, row.inbound);
    addInto(acc.pickupsTotal, computeIncludedPickupsTotal(row, included));
    addInto(acc.pickupsUnassigned, row.pickupsUnassigned);
    addInto(acc.pickupsAssigned, row.pickupsAssigned);
    addInto(acc.pickupsCompleted, row.pickupsCompleted);
    addInto(acc.totals, computeAdjustedTotals(row, included));
  }

  return acc;
}

export function buildGroupedSections(
  rows: OutboundPlanningGridRow[],
  hubs: HubDefinition[],
  sort: SortState,
  included: Record<FreightGroupKey, boolean>
): HubGroupedSection[] {
  const totalsCache = new Map<number, OutboundPlanningMetrics>();
  const getTotals = (r: OutboundPlanningGridRow) => {
    let t = totalsCache.get(r.destinationTerminalNumber);
    if (!t) {
      t = computeAdjustedTotals(r, included);
      totalsCache.set(r.destinationTerminalNumber, t);
    }
    return t;
  };

  const sortRows = (list: OutboundPlanningGridRow[]) =>
    [...list].sort((a, b) => compareRowsForSort(a, b, sort, getTotals(a), getTotals(b)));

  const assigned = new Set<number>();
  const sections: HubGroupedSection[] = [];

  const sortedHubs = [...hubs].sort((a, b) => a.name.localeCompare(b.name));

  for (const hub of sortedHubs) {
    const inHub = rows.filter((r) => hub.terminalNumbers.includes(r.destinationTerminalNumber));
    inHub.forEach((r) => assigned.add(r.destinationTerminalNumber));
    if (inHub.length > 0) {
      sections.push({
        hubId: hub.id,
        hubName: hub.name,
        rows: sortRows(inHub),
      });
    }
  }

  const unassigned = rows.filter((r) => !assigned.has(r.destinationTerminalNumber));
  if (unassigned.length > 0) {
    sections.push({
      hubId: null,
      hubName: "Other destinations",
      rows: sortRows(unassigned),
    });
  }

  return sections;
}

export const GRID_COLUMN_COUNT = 22;
